import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SampleBadge } from "../components/kit";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Card, Field, Textarea, PageTitle, Notice, Tag } from "../components/kit";
import { Loading } from "../components/common";
import { api } from "../lib/api";
import { apiFail } from "../lib/p2";
import { useLang, Placeholder } from "./Learning";
import { AccessRequired, isAccessError } from "../components/Access";
import { PreviewPanel, isPreviewLimit, previewCourse } from "../components/PreviewPanel";

export function useMedia(path) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return undefined;
    let u;
    api.get(`/learning/media/${path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [path]);
  return url;
}

function Video({ unit, my, onTime, vref }) {
  const { t } = useTranslation();
  const src = useMedia(unit.video_path);
  const en = useMedia(unit.vtt?.en);
  const ru = useMedia(unit.vtt?.ru);
  const lang = useLang();
  if (!unit.video_path) return null;
  if (!src) return <Loading />;
  return (
    <video ref={vref} controls playsInline className="aspect-video w-full rounded-xl bg-black" data-testid="lesson-video" src={src}
      onLoadedMetadata={(e) => { if (my.position_sec) e.currentTarget.currentTime = my.position_sec; }} onTimeUpdate={(e) => onTime(e.currentTarget.currentTime)}>
      {en && <track kind="subtitles" srcLang="en" label={`${t("l.subtitles")} EN`} src={en} default={lang === "en"} />}
      {ru && <track kind="subtitles" srcLang="ru" label={`${t("l.subtitles")} RU`} src={ru} default={lang === "ru"} />}
    </video>
  );
}

function Section({ title, children, testid }) {
  if (!children || (Array.isArray(children) && !children.length)) return null;
  return <div data-testid={testid}><h3 className="text-sm font-bold">{title}</h3><div className="text-sm">{children}</div></div>;
}

export default function LessonPage() {
  const { vid, key } = useParams();
  const [sp] = useSearchParams();
  const from = sp.get("from");
  const { t } = useTranslation();
  const lang = useLang();
  const [d, setD] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [note, setNote] = useState("");
  const [label, setLabel] = useState("");
  const vref = useRef(null);
  const last = useRef(0);
  const seeded = useRef("");
  const load = useCallback(() => api.get(`/learning/units/${vid}/${key}`, { params: { lang } }).then((r) => {
    setD(r.data);
    if (seeded.current !== `${vid}/${key}`) { seeded.current = `${vid}/${key}`; setNote(r.data.my.note || ""); }
  }).catch((e) => { if (isPreviewLimit(e)) setPv(previewCourse(e) || true); else if (isAccessError(e)) setBlocked(true); else apiFail(t)(e); }), [vid, key, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pv, setPv] = useState(null);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.put(`/learning/units/${vid}/${key}/progress`, {}).catch(() => {}); }, [vid, key]);
  if (pv) return <div className="mx-auto max-w-3xl" data-testid="lesson-preview-locked"><PreviewPanel courseId={pv === true ? null : pv} /></div>;
  if (blocked) return <AccessRequired area="course" testid="lesson-access-required" />;
  if (!d) return <Loading />;
  const u = d.unit, c = u.content || {};
  const seq = Array.isArray(c.sequence) ? c.sequence : String(c.sequence || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const onTime = (s) => { if (Math.abs(s - last.current) >= 5) { last.current = s; api.put(`/learning/units/${vid}/${key}/progress`, { position_sec: s }).catch(() => {}); } };
  const now = () => vref.current?.currentTime || 0;
  const bookmark = () => api.post(`/learning/units/${vid}/${key}/bookmarks`, { at_sec: now(), label }).then((r) => { setD({ ...d, my: { ...d.my, bookmarks: r.data.bookmarks } }); setLabel(""); }).catch(apiFail(t));
  const done = () => api.put(`/learning/units/${vid}/${key}/progress`, { completed: true, position_sec: now() }).then(() => { toast.success(t("l.v_completed")); load(); }).catch(apiFail(t));
  const download = () => api.get(`/learning/units/${vid}/${key}/download`, { responseType: "blob" }).then((r) => { const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${key}.txt`; a.click(); });
  const addPlan = () => api.post(`/programmes/${from}/learned-elements`, { version_id: vid, unit_key: key }).then(() => toast.success(t("l.added_to_plan", { v: d.version_number }))).catch(apiFail(t));
  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="lesson-page">
      {from && <Link to={`/programmes/${from}`} className="inline-flex min-h-[44px] items-center underline" data-testid="back-to-programme">← {t("l.back_programme")}</Link>}
      <PageTitle title={c.title} sub={`${key} · ${t("l.version", { v: d.version_number })} · ${t("l.author")}: ${c.author || "—"}`} badge={d.sample ? <SampleBadge /> : d.is_demo ? <Placeholder /> : null} />
      <img src="/img/b9/lesson_header.webp" alt="" aria-hidden className="h-32 w-full rounded-2xl object-cover sm:h-40" data-testid="lesson-header-img" />
      {d.my.position_sec > 0 && <p className="text-sm" data-testid="resume-at">{t("l.resume_at", { s: Math.round(d.my.position_sec) })}</p>}
      <Video unit={u} my={d.my} onTime={onTime} vref={vref} />
      <Card className="space-y-3">
        <Section title={t("l.objectives")} testid="lesson-objectives">{c.objectives}</Section>
        <p className="whitespace-pre-line" data-testid="lesson-body">{c.body}</p>
        <Section title={t("l.sequence")}>{seq.length > 0 && <ol className="ml-5 list-decimal" data-testid="lesson-sequence">{seq.map((s, i) => <li key={i}>{s}</li>)}</ol>}</Section>
        <Section title={t("l.limitations")} testid="lesson-limitations">{c.limitations}</Section>
        <Section title={t("l.examples")}>{c.examples}</Section>
        <Section title={t("l.sources")}>{c.sources}</Section>
        <Section title={t("l.related")}>{(u.related_directions || []).join(", ")}</Section>
      </Card>
      <Card className="space-y-2"><h2 className="font-bold">{t("l.bookmarks")}</h2>
        <ul className="space-y-1" data-testid="bookmarks">{(d.my.bookmarks || []).map((b) => <li key={b.id} className="flex items-center gap-2 text-sm">
          <button className="min-h-[44px] underline" onClick={() => { if (vref.current) vref.current.currentTime = b.at_sec; }}>{Math.round(b.at_sec)} s</button>{b.label}</li>)}</ul>
        <div className="flex flex-wrap gap-2"><input aria-label={t("l.add_bookmark")} className="min-h-[44px] flex-1 rounded-xl border px-3" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="bookmark-label" />
          <Btn variant="outline" testid="bookmark-add" onClick={bookmark}>{t("l.add_bookmark")}</Btn></div></Card>
      <Card className="space-y-2"><Field label={t("l.note")} htmlFor="ln"><Textarea id="ln" data-testid="lesson-note" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <Btn variant="outline" testid="note-save" onClick={() => api.put(`/learning/units/${vid}/${key}/note`, { note }).then(() => toast.success(t("common.saved"))).catch(apiFail(t))}>{t("l.save_note")}</Btn></Card>
      <Notice>{t("l.viewed_note")}</Notice>
      <div className="flex flex-wrap gap-2">
        <Btn testid="lesson-done" onClick={done}>{d.my.completed_at ? t("l.v_completed") : t("l.mark_done")}</Btn>
        <Btn variant="outline" testid="lesson-download" onClick={download}>{t("l.download")}</Btn>
        {from && <Btn variant="outline" testid="add-to-plan" onClick={addPlan}>{t("l.add_to_plan")}</Btn>}
        <Tag>{t("l.ask_mentor")}</Tag>
        <Link to={`/learning/courses/${d.course_id}`} className="inline-flex min-h-[44px] items-center underline" data-testid="back-to-course">{t("common.back")}</Link>
      </div>
      {d.demo_preview && u.type === "lesson" && <PreviewPanel courseId={d.course_id} testid="lesson-preview-end" />}
    </div>
  );
}
